"use client";

import { useEffect, useState } from "react";
import { ShieldCheck, Zap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Vpngenixhero() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.3
  });
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: paraRef,
    isVisible: paraVisible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const {
    ref: specsRef,
    isVisible: specsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const [serversActive, setServersActive] = useState(1400);
  const [usersProtected, setUsersProtected] = useState(47000);
  const [uptimeTotal, setUptimeTotal] = useState(99);
  useEffect(() => {
    const interval = setInterval(() => {
      setServersActive(v => v + (Math.random() > 0.7 ? 1 : 0));
      setUsersProtected(v => v + (Math.random() > 0.8 ? 10 : 0));
      setUptimeTotal(v => v < 99.99 ? v + 0.01 : v);
    }, 8000);
    return () => clearInterval(interval);
  }, []);
  const tickerItems = [`SERVERS ACTIVE: ${serversActive}`, `USERS PROTECTED: ${usersProtected.toLocaleString()}`, "ENCRYPTION: AES-256-GCM", `NETWORK UPTIME: ${uptimeTotal.toFixed(2)}%`, "NO-LOGS POLICY", "23 YEARS OF PRIVACY"];
  const specs = [{
    label: "GLOBAL NODES",
    value: "140"
  }, {
    label: "SPEED (GBPS)",
    value: "10"
  }, {
    label: "PROTOCOLS",
    value: "05"
  }];
  return <section data-section-id="1883" id="sovereigntyhero" className="relative w-full overflow-hidden bg-background text-foreground">
      {}
      <div className="absolute inset-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/secure_network_infrastructure.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/secure_network_infrastructure.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/secure_network_infrastructure.png-wvc-sizes" alt="Digital network infrastructure representing secure VPN connections" loading="lazy" decoding="async" className="h-full w-full object-cover opacity-20 mix-blend-luminosity" />
        <div className="absolute inset-0 bg-background/80" />
        {}
        <div className="absolute inset-x-0 bottom-12 h-1/3 opacity-[0.04]" style={{
        backgroundImage: "repeating-linear-gradient(45deg, hsl(221 40% 4%) 0 1px, transparent 1px 14px), repeating-linear-gradient(-45deg, hsl(221 40% 4%) 0 1px, transparent 1px 14px)"
      }} />
      </div>

      {}
      <div className="relative h-3 w-full bg-primary" />

      {}
      <div className="relative px-6 md:px-10 lg:px-16 pt-24 pb-32 md:pt-36 md:pb-44">
        <div className="mx-auto max-w-[1360px] grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {}
          <div className="lg:col-span-8">
            {}
            <div ref={eyebrowRef} className={`flex items-center gap-3 font-mono text-xs md:text-sm uppercase tracking-[0.18em] text-primary transition-all duration-500 ease-out ${eyebrowVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
              <span className="inline-block h-2.5 w-2.5 rounded-lg bg-primary" />
              <span>VPNGENIX / GLOBAL SECURITY — EST. 2001</span>
            </div>

            {}
            <h1 ref={headlineRef} className="font-sans font-default mt-6 uppercase font-black text-foreground" style={{
            fontSize: "clamp(2.5rem, 7vw, 6.5rem)",
            lineHeight: 0.92,
            letterSpacing: "-0.02em"
          }}>
              <span className="block overflow-hidden">
                <span className={`block transition-transform duration-700 ease-[cubic-bezier(0.2,0.7,0.2,1)] ${headlineVisible ? "translate-y-0" : "translate-y-full"}`}>
                  Total Digital
                </span>
              </span>
              <span className="block overflow-hidden">
                <span className={`block transition-transform duration-700 delay-100 ease-[cubic-bezier(0.2,0.7,0.2,1)] ${headlineVisible ? "translate-y-0" : "translate-y-full"}`}>
                  Freedom.{" "}
                  <span className="relative inline-block">
                    <span aria-hidden="true" className={`absolute left-0 top-[15%] bottom-[15%] bg-primary transition-[width] duration-500 ease-[cubic-bezier(0.2,0.7,0.2,1)] -z-0 ${headlineVisible ? "w-full" : "w-0"}`} style={{
                    transitionDelay: "600ms"
                  }} />
                    <span className="relative z-10 text-white px-2">Privacy</span>
                  </span>
                  .
                </span>
              </span>
              <span className="block overflow-hidden">
                <span className={`block transition-transform duration-700 delay-200 ease-[cubic-bezier(0.2,0.7,0.2,1)] ${headlineVisible ? "translate-y-0" : "translate-y-full"}`}>
                  Guaranteed.
                </span>
              </span>
            </h1>

            {}
            <div className={`mt-6 h-2 w-32 bg-primary transition-all duration-500 ${headlineVisible ? "opacity-100" : "opacity-0"}`} />

            {}
            <p ref={paraRef} className={`mt-8 font-mono text-base md:text-lg text-muted-foreground max-w-[52ch] leading-relaxed font-normal transition-all duration-500 delay-200 ${paraVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
            fontFamily: "var(--font-mono)"
          }}>
              Enterprise-grade encryption for your personal data. Secure your connection, 
              bypass geo-restrictions, and browse with absolute anonymity. Trusted by 
              millions to keep the internet open and private.
            </p>

            {}
            <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center gap-3">
                <Button asChild className="h-auto rounded-lg bg-primary text-white hover:bg-primary/90 uppercase font-black tracking-[0.06em] px-8 py-4 text-sm transition-all duration-200">
                  <Link to="https://www.jdoqocy.com/click-101434144-15314137" newTab>
                    <Zap className="size-4" strokeWidth={1.75} />
                    Get Protected
                  </Link>
                </Button>
                <span className="font-mono text-xs uppercase tracking-[0.12em] text-primary border border-dashed border-primary/60 px-2.5 py-1">
                  SECURE
                </span>
              </div>
              <Button asChild variant="outline" className="h-auto rounded-lg bg-transparent border-2 border-foreground text-foreground hover:bg-transparent hover:text-primary hover:border-primary uppercase font-mono font-bold tracking-[0.06em] px-8 py-4 text-sm transition-colors duration-200">
                <Link to="https://www.jdoqocy.com/click-101434144-15314137" newTab>
                  <ShieldCheck className="size-4" strokeWidth={1.75} />
                  View Security
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={specsRef} className="lg:col-span-4 lg:pl-6 flex flex-col gap-4">
            {specs.map((spec, i) => <div data-index={i} key={spec.label} className={`relative bg-card border border-border p-6 group hover:border-primary/40 transition-all duration-300 ${specsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDelay: `${300 + i * 120}ms`
          }}>
                <div className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  {spec.label}
                </div>
                <div className="font-sans font-default font-black text-primary mt-2 leading-none" style={{
              fontSize: "clamp(3rem, 5vw, 4.5rem)"
            }}>
                  {spec.value}
                </div>
              </div>)}

            {}
            <div className="bg-primary text-white p-5 mt-2 rounded-lg">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] mb-1">
                SUPPORT STATUS
              </div>
              <div className="font-mono text-sm font-bold">
                24/7 LIVE CHAT · GLOBAL SUPPORT
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="relative h-[52px] w-full bg-primary text-white overflow-hidden border-t-2 border-background">
        <div className="absolute inset-0 flex items-center whitespace-nowrap">
          <div className="flex items-center gap-0 font-mono font-bold uppercase tracking-[0.12em] text-sm" style={{
          animation: "ticker-scroll 30s linear infinite"
        }}>
            {[...tickerItems, ...tickerItems, ...tickerItems].map((item, i) => <span data-index={i} key={i} className="flex items-center px-6">
                <span className="inline-block h-2 w-2 rounded-lg bg-white mr-6" />
                {item}
              </span>)}
          </div>
        </div>
        <style>{`
          @keyframes ticker-scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-33.333%); }
          }
        `}</style>
      </div>
    </section>;
}